import { useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useSearchParams } from 'react-router-dom'
import { getScratchCampaignDashboard } from '@/lib/api/scratch-campaigns.service'
import { ApiClientError } from '@/lib/api/client'
import { useAuth } from '@/hooks/useAuth'
import { useRaspadinhaCampaigns } from '@/contexts/RaspadinhaCampaignsContext'
import { canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import listStyles from '../CampanhasPage/CampanhasPage.module.css'

export function CampanhaRaspadinhaDashboardTab() {
  const [searchParams] = useSearchParams()
  const { getById } = useRaspadinhaCampaigns()
  const { isCollaborator, hasPermission } = useAuth()
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)
  const [metrics, setMetrics] = useState<{
    cartelas_vendidas: number
    cartelas_raspadas: number
    horarios_semanais: number
    ganhadores_cadastrados: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const campaignId = useMemo(() => {
    const rawValue = searchParams.get('id')
    if (!rawValue) return null
    const parsed = Number(rawValue)
    return Number.isFinite(parsed) ? parsed : null
  }, [searchParams])

  const campaign = campaignId !== null ? getById(campaignId) : null

  useEffect(() => {
    if (!canViewDashboard || campaignId === null) {
      setMetrics(null)
      setErrorMessage(null)
      return
    }

    let cancelled = false

    const loadDashboard = async () => {
      setIsLoading(true)
      setErrorMessage(null)
      try {
        const data = await getScratchCampaignDashboard(campaignId)
        if (!cancelled) {
          setMetrics(data)
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof ApiClientError
              ? error.message
              : 'Não foi possível carregar o dashboard da campanha raspadinha.'
          setErrorMessage(message)
          setMetrics(null)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadDashboard()

    return () => {
      cancelled = true
    }
  }, [campaignId, canViewDashboard])

  if (!canViewDashboard) {
    return (
      <div className={listStyles.emptyState}>
        <p>
          Você não tem permissão para visualizar o dashboard desta campanha raspadinha. Peça ao
          dono da instituição para habilitar a permissão Visualizar dashboard e métricas.
        </p>
      </div>
    )
  }

  if (campaignId === null) {
    return (
      <div className={listStyles.emptyState}>
        <p>Selecione uma campanha raspadinha para ver o dashboard.</p>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className={listStyles.emptyState}>
        <p>Campanha raspadinha não encontrada.</p>
      </div>
    )
  }

  return (
    <div className={listStyles.page}>
      <div className={listStyles.panelCard}>
        <div className={listStyles.panelHeader}>
          <div>
            <h3 className={listStyles.sectionTitle}>Dashboard — {campaign.name}</h3>
            <p className={listStyles.sectionSubtitle}>
              Métricas em tempo real da campanha raspadinha.
            </p>
          </div>
        </div>

        {isLoading ? (
          <p className={listStyles.sectionSubtitle} role="status">
            Carregando métricas...
          </p>
        ) : null}

        {errorMessage ? (
          <p className={listStyles.sectionSubtitle} role="alert">
            {errorMessage}
          </p>
        ) : null}

        {metrics ? (
          <div className={listStyles.statsRow}>
            <div className={listStyles.statItem}>
              <div className={listStyles.statIcon}>
                <Icon icon="mdi:ticket-outline" />
              </div>
              <div className={listStyles.statContent}>
                <span>Cartelas vendidas</span>
                <strong>{metrics.cartelas_vendidas}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconActive}`}>
                <Icon icon="mdi:gesture-tap" />
              </div>
              <div className={listStyles.statContent}>
                <span>Cartelas raspadas</span>
                <strong>{metrics.cartelas_raspadas}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconFinished}`}>
                <Icon icon="mdi:trophy" />
              </div>
              <div className={listStyles.statContent}>
                <span>Horários semanais</span>
                <strong>{metrics.horarios_semanais}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconDraft}`}>
                <Icon icon="mdi:clock-check-outline" />
              </div>
              <div className={listStyles.statContent}>
                <span>Ganhadores cadastrados</span>
                <strong>{metrics.ganhadores_cadastrados}</strong>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
